/**
 * On-demand loader for third-party SDK scripts (recruiter chat, story feed…).
 *
 * These SDKs used to be `beforeInteractive` in `_document`, which put several
 * hundred KB of uncompressed JS ahead of hydration on every page. Loading them
 * here instead keeps them off the critical path: each `src` is injected at most
 * once per session and every caller shares the same promise.
 */

const pending = new Map<string, Promise<void>>();

/**
 * Resolves once the page has finished loading and the main thread is idle, so
 * a heavy SDK never competes with hydration or the first interaction.
 */
export const whenIdle = (): Promise<void> =>
  new Promise(resolve => {
    if (typeof window === 'undefined') return resolve();

    const scheduleIdle = () => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => resolve(), {timeout: 3000});
      } else {
        setTimeout(resolve, 1);
      }
    };

    if (document.readyState === 'complete') {
      scheduleIdle();
    } else {
      window.addEventListener('load', scheduleIdle, {once: true});
    }
  });

/**
 * Injects `<script async src>` once. Rejects on SSR, a missing `src`, or a
 * network error; a failed load is evicted so a later call can retry.
 */
export const loadExternalScript = (src?: string): Promise<void> => {
  if (typeof window === 'undefined' || !src) {
    return Promise.reject(new Error('loadExternalScript: unavailable'));
  }

  const existing = pending.get(src);
  if (existing) return existing;

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => {
      pending.delete(src);
      script.remove();
      reject(new Error(`loadExternalScript: failed ${src}`));
    };
    document.head.appendChild(script);
  });

  pending.set(src, promise);
  return promise;
};
